import type { CSSProperties, ReactNode } from 'react';
import type { HandleType, Position } from '../../core';
import { useHandleConnect } from '../../interactions';
import { selectClickConnectStart, selectConnectOnClick, useMindmapStore } from '../../store';
import { classNames } from '../../utils';
import { useNodeId } from '../NodeIdContext';

export type HandleProps = {
  type: HandleType;
  position: Position;
  id?: string | null;
  isConnectable?: boolean;
  isConnectableStart?: boolean;
  isConnectableEnd?: boolean;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
};

/**
 * A connection point on a node's side: drag from it to connect, or click it and then another handle.
 * Like v11 it does not check `nodesConnectable` — that prop only hides the connection line.
 */
export const Handle = ({
  type,
  position,
  id = null,
  isConnectable = true,
  isConnectableStart = true,
  isConnectableEnd = true,
  style,
  className,
  children,
}: HandleProps) => {
  const nodeId = useNodeId();
  const connectOnClick = useMindmapStore(selectConnectOnClick);
  const clickStart = useMindmapStore(selectClickConnectStart);
  const { onPointerDown, onClick } = useHandleConnect({ nodeId, handleId: id, type, isConnectableStart });
  const isClickStart = clickStart?.nodeId === nodeId && clickStart.handleId === id && clickStart.type === type;

  return (
    <div
      data-handleid={id ?? undefined}
      data-nodeid={nodeId ?? undefined}
      data-handlepos={position}
      className={classNames(
        'cm-handle',
        `cm-handle--${position}`,
        'nodrag',
        'nopan',
        type,
        isConnectable && 'connectable',
        isConnectableStart && 'connectablestart',
        isConnectableEnd && 'connectableend',
        isConnectable && 'connectionindicator',
        isClickStart && 'connecting',
        className,
      )}
      style={style}
      onPointerDown={onPointerDown}
      onClick={connectOnClick ? onClick : undefined}
    >
      {children}
    </div>
  );
};
